export function snapshotAiOverview(args = {}) {
  return {
    open: false,
    loading: true,
    generating: false,
    summary: null,
    squadIds: args.squadIds || [],
    tagIds: args.tagIds || [],
    snapshotId: args.snapshotId,
    endpointUrl: args.endpointUrl,
    snapshotBranchId: args.snapshotBranchId || null,
    isAcked: args.isAcked || false,

    // Dynamic values come from parent snapshotSentiment component via Alpine.js expressions
    // in the ERB template: dxiComparisonValue, compareTo, dxiScore
    // These are automatically reactive and update when the parent component changes

    get aiOverviewKey() {
      return "snapshot_ai_overview_" + this.snapshotId;
    },

    ackOverview() {
      if (!this.isAcked) {
        try {
          window.ack(this.aiOverviewKey);
          this.isAcked = true;
        } catch (error) {
          console.error("Failed to acknowledge overview:", error);
        }
      }
    },

    toggleSummary() {
      if (!this.isAcked) this.ackOverview();
      this.open = !this.open;
      localStorage.setItem(this.aiOverviewKey, this.open);
    },

    openSummary() {
      if (!this.isAcked) this.ackOverview();
      this.open = true;
      localStorage.setItem(this.aiOverviewKey, this.open);
    },

    getRequestParams() {
      const params = {};
      if (this.squadIds && this.squadIds.length > 0)
        params.squad_ids = this.squadIds;
      if (this.tagIds && this.tagIds.length > 0) params.tag_ids = this.tagIds;

      // Include the current branch from the URL to preserve branch context
      const urlParams = new URLSearchParams(window.location.search);
      const branchParam = urlParams.get("branch");
      if (branchParam) params.branch = branchParam;

      return params;
    },

    handleResponse(response) {
      if (!response.ok) {
        this.summary = null;
        return Promise.reject(new Error("Request failed"));
      }
      return response.json();
    },

    processData(data) {
      if (data && data.error) {
        this.summary = null;
        console.error("Error:", data.error);
      } else if (data) {
        this.summary = data;
        // Auto-open after successful generation
        if (this.generating) {
          this.openSummary();
        }
      }
      this.loading = false;
      this.generating = false;
    },

    handleError() {
      this.summary = null;
      this.loading = false;
      this.generating = false;
    },

    fetchSummary() {
      this.loading = true;
      const url = new URL(this.endpointUrl, window.location.origin);
      const params = this.getRequestParams();

      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // Use the array notation for consistent backend processing
          // or fingerprinting gets flaky
          value.forEach((v) => url.searchParams.append(`${key}[]`, v));
        } else {
          url.searchParams.set(key, value);
        }
      });

      fetch(url.toString())
        .then(this.handleResponse.bind(this))
        .then(this.processData.bind(this))
        .catch(this.handleError.bind(this));
    },

    createSummary() {
      this.generating = true;
      const params = this.getRequestParams();

      fetch(this.endpointUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(params),
      })
        .then(this.handleResponse.bind(this))
        .then(this.processData.bind(this))
        .catch(this.handleError.bind(this));
    },

    init() {
      // Set initial open state based on ack status and localStorage
      if (this.isAcked) {
        this.open = localStorage.getItem(this.aiOverviewKey) === "true";
      }
      this.fetchSummary();
      this.squadHandler = (event) => {
        this.squadIds = event.detail;
        this.fetchSummary();
      };
      this.tagHandler = (event) => {
        this.tagIds = event.detail;
        this.fetchSummary();
      };
      window.addEventListener("change-squad-ids", this.squadHandler);
      window.addEventListener("change-tag-ids", this.tagHandler);
    },

    destroy() {
      window.removeEventListener("change-squad-ids", this.squadHandler);
      window.removeEventListener("change-tag-ids", this.tagHandler);
    },

    get roundedDxiComparisonValue() {
      return Math.round(this.dxiComparisonValue || 0);
    },

    get absoluteDxiComparisonValue() {
      return Math.abs(this.roundedDxiComparisonValue);
    },

    get dxiComparisonLabel() {
      if (
        typeof this.roundedDxiComparisonValue === "undefined" ||
        this.roundedDxiComparisonValue === null
      ) {
        return "unknown";
      }
      if (this.roundedDxiComparisonValue < -1) {
        return "below average";
      }
      if (this.roundedDxiComparisonValue > 1) {
        return "above average";
      }
      return "average";
    },

    get comparisonAmountText() {
      return this.absoluteDxiComparisonValue !== 0
        ? this.absoluteDxiComparisonValue
        : "";
    },

    get comparisonDirectionText() {
      if (this.roundedDxiComparisonValue === 0) {
        return "on par with the";
      }
      return this.roundedDxiComparisonValue >= 0
        ? "points above"
        : "points below";
    },

    get percentileLabel() {
      return this.compareTo.replace("vs_", "");
    },

    get vsCompareToLabel() {
      if (this.compareTo === "vs_org") {
        return "organization average";
      }

      return "industry " + this.percentileLabel + " percentile";
    },

    get efficiencyLevelText() {
      if (this.roundedDxiComparisonValue > 0) {
        return "strong performance.";
      }
      if (this.roundedDxiComparisonValue < 0) {
        return "an opportunity to improve performance.";
      }
      return "average performance.";
    },

    get showTeaser() {
      return !this.isAcked && this.summary;
    },

    get showExpanded() {
      return this.isAcked && this.open && this.summary;
    },
  };
}
